import ContentIllustration from '../../components/ui/ContentIllustration';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Navigate, Link } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import { getLogoTarget } from '../../components/layout/Header';
import { Loader2, CheckCircle, AlertTriangle, Gift, Share2, ArrowRight } from 'lucide-react';

// Page unifiée « Activer un code » : code CADEAU (abonnement / formateur) OU code de PARTAGE (thématique).
export default function GiftRedeem() {
    const { user, fetchProfile } = useAuthStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const urlCode = searchParams.get('code') || '';
    const urlType = searchParams.get('type') || ''; // 'partage' pour un deep-link de code thématique

    const [cadeauCode, setCadeauCode] = useState('');
    const [partageCode, setPartageCode] = useState('');
    const [status, setStatus] = useState(urlCode ? 'loading' : 'idle');
    const [message, setMessage] = useState('');
    const [resultData, setResultData] = useState(null);
    const [resultKind, setResultKind] = useState('cadeau'); // 'cadeau' | 'partage' — pilote l'écran de succès
    const [submitting, setSubmitting] = useState(false);
    const [codeInfo, setCodeInfo] = useState(null);
    const [consentChecked, setConsentChecked] = useState(false);
    const redeemedRef = useRef(false);

    // Activation auto si code dans l'URL (lien cadeau → cadeau ; ?type=partage → partage)
    useEffect(() => {
        if (!user || !urlCode) return;
        if (redeemedRef.current) return;
        redeemedRef.current = true;
        if (urlType === 'partage') redeemShareCode(urlCode);
        else loadAndMaybeRedeem(urlCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, urlCode]);

    // ===== CODE CADEAU =====
    const attemptRedeem = async (rawCode, consent = false) => {
        const code = (rawCode || '').trim().toUpperCase();
        if (!code) return;
        setStatus('loading');
        setMessage('');
        try {
            const { data } = await api.post('/gift/redeem.php', { code, formateur_consent: consent });
            if (data.success) {
                // Backend a auto-ajouté le scope dans dashboard_prefs.selectedScopes.
                localStorage.removeItem('monquiz_dashboard_prefs');
                await fetchProfile();
                setResultKind('cadeau');
                setStatus('success');
                setResultData(data.data);
                if (data.data.already_active) {
                    const fin = data.data.fin
                        ? new Date(data.data.fin + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                        : null;
                    setMessage(fin
                        ? `Ton abonnement « ${data.data.scope_label} » est actif jusqu'au ${fin}`
                        : `Ton abonnement « ${data.data.scope_label} » est déjà actif`);
                } else {
                    const fin = new Date(data.data.fin + 'T00:00:00').toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                    });
                    setMessage(`Abonnement « ${data.data.scope_label} » activé jusqu'au ${fin}`);
                }
            }
        } catch (e) {
            setStatus('error');
            setMessage(e.response?.data?.message || 'Code invalide ou expiré');
        }
    };

    // Pré-vérifie le code : si c'est un code formateur, on demande le consentement RGPD avant d'activer.
    const loadAndMaybeRedeem = async (rawCode) => {
        const code = (rawCode || '').trim().toUpperCase();
        if (!code) return;
        setStatus('loading');
        setMessage('');
        try {
            const { data } = await api.get('/gift/info.php', { params: { code } });
            if (data.success && data.data?.is_formateur_code) {
                setCodeInfo({ ...data.data, code });
                setConsentChecked(false);
                setStatus('consent');
                return;
            }
        } catch {
            // info indisponible → on tente l'activation directe (codes non-formateur)
        }
        attemptRedeem(code);
    };

    // ===== CODE DE PARTAGE (thématique) =====
    const redeemShareCode = async (rawCode) => {
        const code = (rawCode || '').trim().toUpperCase();
        if (!code) return;
        setStatus('loading');
        setMessage('');
        try {
            const { data } = await api.post('/share/codes.php', { action: 'redeem', code });
            if (data.success) {
                setResultKind('partage');
                setStatus('success');
                setResultData(data.data);
                setMessage(`Accès activé pour « ${data.data.scope_label} » jusqu'au ${new Date(data.data.access_end).toLocaleDateString('fr-FR')}`);
            }
        } catch (e) {
            setStatus('error');
            setMessage(e.response?.data?.error || e.response?.data?.message || 'Code invalide ou expiré');
        }
    };

    const submitCadeau = async (e) => {
        e?.preventDefault();
        const c = cadeauCode.trim().toUpperCase();
        if (!c) return;
        // On active ici directement → l'effet URL ne doit PAS re-déclencher (sinon double appel).
        redeemedRef.current = true;
        setSubmitting(true);
        setSearchParams({ code: c }, { replace: true });
        await loadAndMaybeRedeem(c);
        setSubmitting(false);
    };
    const submitPartage = async (e) => {
        e?.preventDefault();
        const c = partageCode.trim().toUpperCase();
        if (!c) return;
        // Idem : on garde le ref pour empêcher l'effet URL de relancer un 2e redeem.
        redeemedRef.current = true;
        setSubmitting(true);
        setSearchParams({ code: c, type: 'partage' }, { replace: true });
        await redeemShareCode(c);
        setSubmitting(false);
    };
    const codeInputStyle = {
        padding: '12px 14px', fontSize: '1em',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: 1,
        textAlign: 'center', border: '2px solid var(--border)', borderRadius: 10,
        background: 'var(--card-bg)', color: 'inherit', boxSizing: 'border-box', flex: 1, minWidth: 0,
    };
    const codeCardStyle = {
        textAlign: 'left', padding: '14px 16px', border: '1px solid var(--card-border, var(--border))',
        borderRadius: 12, background: 'var(--surface, #fff)',
    };

    const handleRetry = () => {
        setStatus('idle');
        setMessage('');
        setCadeauCode('');
        setPartageCode('');
        setCodeInfo(null);
        setConsentChecked(false);
        setResultData(null);
        setSearchParams({}, { replace: true });
        redeemedRef.current = false;
    };

    if (!user) {
        const email = searchParams.get('email') || '';
        const back = urlCode
            ? `/compte/code?code=${urlCode}${urlType === 'partage' ? '&type=partage' : ''}${email ? `&email=${encodeURIComponent(email)}` : ''}`
            : '/compte/code';
        const qs = new URLSearchParams({ redirect: back });
        if (email) qs.set('email', email);
        return <Navigate to={`/connexion?${qs.toString()}`} replace />;
    }

    return (
        <div className="auth-page" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <div style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
                <ContentIllustration name="ticket" size={64} style={{ color: '#7c3aed', marginBottom: 12 }} />
                <h1 style={{ fontSize: '1.5rem', margin: '0 0 8px' }}>Activer un code</h1>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.92em', margin: '0 0 24px' }}>
                    Saisis ton code (reçu par email ou via un lien) pour activer ton accès.
                </p>

                {/* IDLE : deux formulaires distincts (cadeau / partage) */}
                {status === 'idle' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {/* Code CADEAU → abonnement offert / formateur */}
                        <form onSubmit={submitCadeau} style={codeCardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <Gift size={16} style={{ color: 'var(--primary, #7c3aed)' }} />
                                <strong style={{ fontSize: '0.95em' }}>Code cadeau</strong>
                            </div>
                            <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                                Active un abonnement offert (ou un code formateur).
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type="text"
                                    value={cadeauCode}
                                    onChange={(e) => setCadeauCode(e.target.value.toUpperCase())}
                                    placeholder="EX: ABCD-EFGH-IJKL-MNOP"
                                    style={codeInputStyle}
                                />
                                <button type="submit" className="btn btn-primary" disabled={!cadeauCode.trim() || submitting}>
                                    {submitting ? <Loader2 size={16} className="spin" /> : 'Activer'}
                                </button>
                            </div>
                        </form>

                        {/* Code de PARTAGE → accès lecture seule à une thématique */}
                        <form onSubmit={submitPartage} style={codeCardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <Share2 size={16} style={{ color: 'var(--primary, #7c3aed)' }} />
                                <strong style={{ fontSize: '0.95em' }}>Code thématique (partage)</strong>
                            </div>
                            <p style={{ fontSize: '0.82em', color: 'var(--text-muted)', margin: '0 0 10px' }}>
                                Accède à une thématique partagée avec toi.
                            </p>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    type="text"
                                    value={partageCode}
                                    onChange={(e) => setPartageCode(e.target.value.toUpperCase())}
                                    placeholder="EX: ABCD-EFGH-IJKL-MNOP"
                                    style={codeInputStyle}
                                />
                                <button type="submit" className="btn btn-primary" disabled={!partageCode.trim() || submitting}>
                                    {submitting ? <Loader2 size={16} className="spin" /> : 'Activer'}
                                </button>
                            </div>
                        </form>

                        <Link to={getLogoTarget(user)} style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
                            ← Retour à mes thématiques
                        </Link>
                    </div>
                )}

                {status === 'consent' && codeInfo && (
                    <div style={{ textAlign: 'left' }}>
                        <div style={{ padding: '14px 16px', background: 'rgba(124,58,237,0.06)', border: '1px solid var(--card-border)', borderRadius: 10, marginBottom: 16 }}>
                            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
                                Ce code donne acc&egrave;s &agrave; &laquo;&nbsp;{codeInfo.scope_label}&nbsp;&raquo;.
                            </p>
                            <p style={{ margin: 0, fontSize: '0.9em', color: 'var(--text-muted)' }}>
                                Il a &eacute;t&eacute; distribu&eacute; par votre formateur{codeInfo.formateur_name ? ` ${codeInfo.formateur_name}` : ''}.
                                En l'activant, vous acceptez que ce formateur puisse <strong>consulter vos r&eacute;sultats sur cette th&eacute;matique</strong>
                                {' '}(quiz r&eacute;alis&eacute;s, scores, progression) &mdash; et uniquement sur celle-ci.
                            </p>
                        </div>
                        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.9em', marginBottom: 16, cursor: 'pointer' }}>
                            <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} style={{ marginTop: 3 }} />
                            <span>J'accepte que mon formateur consulte mes r&eacute;sultats sur cette th&eacute;matique.</span>
                        </label>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button className="btn btn-primary" disabled={!consentChecked} onClick={() => attemptRedeem(codeInfo.code, true)}>
                                Activer mon acc&egrave;s
                            </button>
                            <button className="btn btn-secondary" onClick={handleRetry}>Annuler</button>
                        </div>
                    </div>
                )}

                {status === 'loading' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <Loader2 size={20} className="spin" />
                        <span>Activation en cours…</span>
                    </div>
                )}

                {status === 'success' && resultKind === 'partage' && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#10b981', marginBottom: 16 }}>
                            <CheckCircle size={24} />
                            <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Code activé !</span>
                        </div>
                        <p style={{ marginBottom: 24 }}>{message}</p>
                        <Link to="/compte/quiz/mes-thematiques" className="btn btn-primary">
                            Accéder à mes thématiques <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                        </Link>
                    </div>
                )}

                {status === 'success' && resultKind === 'cadeau' && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#10b981', marginBottom: 16 }}>
                            <CheckCircle size={24} />
                            <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {resultData?.already_active ? 'Tout est bon !' : 'Cadeau activé !'}
                            </span>
                        </div>
                        <p style={{ marginBottom: 24 }}>{message}</p>
                        {!resultData?.already_active
                            && (resultData?.max_uses || 0) > 1
                            && typeof resultData?.remaining_uses === 'number'
                            && resultData.remaining_uses < 5 && (
                            <p style={{
                                marginBottom: 20,
                                fontSize: '0.85em',
                                color: resultData.remaining_uses === 0 ? '#94a3b8' : '#f59e0b',
                                fontStyle: 'italic',
                            }}>
                                {resultData.remaining_uses === 0
                                    ? 'Tu as pris la dernière place de ce code, bien joué !'
                                    : `Plus que ${resultData.remaining_uses} place${resultData.remaining_uses > 1 ? 's' : ''} restante${resultData.remaining_uses > 1 ? 's' : ''} sur ce code.`}
                            </p>
                        )}
                        {resultData?.scope_slug && (
                            <Link to={`/compte/quiz/${resultData.scope_slug}`} className="btn btn-primary">
                                Commencer les quiz <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                            </Link>
                        )}
                    </div>
                )}

                {status === 'error' && (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#ef4444', marginBottom: 16 }}>
                            <AlertTriangle size={24} />
                            <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Activation impossible</span>
                        </div>
                        <p style={{ marginBottom: 20, color: 'var(--text-muted)' }}>{message}</p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button onClick={handleRetry} className="btn btn-primary">
                                Essayer un autre code
                            </button>
                            <Link to={getLogoTarget(user)} className="btn btn-secondary">
                                Retour à mes thématiques
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
